import React, { useState, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getProjectPhotos, uploadProjectPhoto, deleteProjectPhoto, updateProjectPhoto } from "@/lib/api";
import { DirectUpload } from "@rails/activestorage";
import { Cropper } from "react-cropper";
// import 'cropperjs/dist/cropper.css'

type ProjectPhotosProps = {
  site: { slug: string };
  project: { id: number };
};


const ProjectPhotos: React.FC<ProjectPhotosProps> = ({ site, project }) => {
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const {
    data: photos,
    isLoading: photosLoading,
    isError: photosError,
    error: photosErrorObj,
  } = useQuery({
    queryKey: ["project-photos", site.slug, project.id],
    queryFn: () => getProjectPhotos(site.slug, project.id),
    enabled: !!project,
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      // Use DirectUpload to upload the file to ActiveStorage
      return new Promise((resolve, reject) => {
        const upload = new DirectUpload(file, "/rails/active_storage/direct_uploads");
        upload.create((error, blob) => {
          if (error) {
            reject(error);
          } else {
            // Send the signed_id to the backend to create the Photo record
            uploadProjectPhoto(site.slug, project.id, blob.signed_id)
              .then(resolve)
              .catch(reject);
          }
        });
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-photos", site.slug, project.id] });
      setSelectedFile(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (photoId: number) => deleteProjectPhoto(site.slug, project.id, photoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-photos", site.slug, project.id] });
    },
  });

  // Cropper modal state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropPhoto, setCropPhoto] = useState<any>(null);
  const cropperRef = useRef<any>(null);
  const [cropError, setCropError] = useState<string | null>(null);
  const [savingCrop, setSavingCrop] = useState(false);

  // Crop mutation
  const cropMutation = useMutation({
    mutationFn: async ({
      photoId,
      crop_x,
      crop_y,
      crop_width,
      crop_height,
    }: {
      photoId: number;
      crop_x: number;
      crop_y: number;
      crop_width: number;
      crop_height: number;
    }) => {
      return updateProjectPhoto(site.slug, project.id, photoId, {
        crop_x,
        crop_y,
        crop_width,
        crop_height,
      });
    },
    onSuccess: () => {
      setCropModalOpen(false);
      setCropPhoto(null);
      setCropError(null);
      queryClient.invalidateQueries({ queryKey: ["project-photos", site.slug, project.id] });
    },
    onError: (error: any) => {
      setCropError(error.message || "Failed to save crop");
    },
    onSettled: () => {
      setSavingCrop(false);
    },
  });

  // Open crop modal for a photo
  const handleOpenCrop = (photo: any) => {
    setCropPhoto(photo);
    setCropModalOpen(true);
    setCropError(null);
  };

  // Save crop coordinates
  const handleSaveCrop = () => {
    if (!cropperRef.current || !cropPhoto) return;
    setSavingCrop(true);
    setCropError(null);
    const cropper = cropperRef.current.cropper;
    const data = cropper.getData(true); // true = rounded values
    cropMutation.mutate({
      photoId: cropPhoto.id,
      crop_x: Math.round(data.x),
      crop_y: Math.round(data.y),
      crop_width: Math.round(data.width),
      crop_height: Math.round(data.height),
    });
  };

  return (
    <div>
      <div className="mb-4 flex flex-col sm:flex-row items-center gap-2 bg-green-100 p-4 rounded">
        <input
          type="file"
          accept="image/*"
          onChange={e => setSelectedFile(e.target.files?.[0] || null)}
          className="block w-48"
        />
        {selectedFile && <Button
          onClick={() => selectedFile && uploadMutation.mutate(selectedFile)}
          disabled={!selectedFile || uploadMutation.status === "pending"}
          className="ml-0 sm:ml-2"
        >
          {uploadMutation.status === "pending" ? "Uploading..." : "Upload"}
        </Button>}
        {uploadMutation.status === "error" && (
          <Alert variant="destructive" className="ml-2">
            {(uploadMutation.error as Error).message}
          </Alert>
        )}
      </div>
      {photosLoading ? (
        <div className="text-muted-foreground">Loading images...</div>
      ) : photosError ? (
        <Alert variant="destructive" className="mb-2">
          {(photosErrorObj as Error).message}
        </Alert>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1 gap-4">
          {photos && photos.length > 0 ? (
            photos.map((photo: any) => (
              <Card key={photo.id} className="flex flex-col items-center p-2">
                <img
                  src={photo.image_url_cropped || photo.image_url || photo.url || photo.image || ""}
                  alt={photo.filename || "Project image"}
                  className="object-contain w-full h-32 mb-2 bg-white border rounded"
                />
                <div className="text-xs text-muted-foreground mb-1">
                  {photo.size ? `${(photo.size / (1024 * 1024)).toFixed(1)} MB` : ""}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-300"
                    onClick={() => deleteMutation.mutate(photo.id)}
                    disabled={deleteMutation.status === "pending"}
                  >
                    Delete
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-blue-300 text-blue-600"
                    onClick={() => handleOpenCrop(photo)}
                  >
                    Crop
                  </Button>
                </div>
              </Card>
            ))
          ) : (
            <div className="col-span-full text-muted-foreground text-center">
              No images uploaded yet.
            </div>
          )}
        </div>
      )}

      {/* Cropper Dialog */}
      <Dialog open={cropModalOpen} onOpenChange={setCropModalOpen}>
        <DialogContent className="max-w-lg w-full">
          <DialogHeader>
            <DialogTitle>Crop Photo</DialogTitle>
          </DialogHeader>
          {cropPhoto && (
            <div>
              <Cropper
                src={cropPhoto.image_url || cropPhoto.url || cropPhoto.image || ""}
                style={{ height: 300, width: "100%" }}
                initialAspectRatio={1}
                viewMode={1}
                guides={true}
                background={false}
                responsive={true}
                autoCropArea={1}
                checkOrientation={false}
                ref={cropperRef}

                onInitialized={instance => {
                  const cropData = cropPhoto.crop_data || {};
                  setTimeout(() => {
                    instance.setCropBoxData(cropData);
                    instance.setData(cropData);
                  }, 100);
                  
                }}
              />
              {cropError && (
                <Alert variant="destructive" className="mt-2">
                  {cropError}
                </Alert>
              )}
              <DialogFooter className="mt-4 flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setCropModalOpen(false)}
                  disabled={savingCrop}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveCrop}
                  disabled={savingCrop}
                >
                  {savingCrop ? "Saving..." : "Save Crop"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectPhotos;
